import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { NarrativeSummary } from '@/lib/types';

/**
 * Hook to handle chapter creation.
 */
export function useCreateChapter() {
  const { backend } = useFirebase();
  const { novel } = useNovel();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);

  const createChapter = async (name?: string, targetOrder?: number) => {
    if (!novel.id) return null;

    setIsCreating(true);
    try {
      const id = uuidv4();
      const existingChapters = Object.values(novel.summaries?.chapters || {})
        .map(s => s as NarrativeSummary)
        .filter(ch => !ch.isArchive)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      let order = existingChapters.length;
      let shiftedChapters: NarrativeSummary[] = [];

      if (typeof targetOrder === 'number') {
        order = targetOrder;
        shiftedChapters = existingChapters
          .filter(ch => (ch.order || 0) >= targetOrder)
          .map(ch => ({
            ...ch,
            order: (ch.order || 0) + 1,
            updatedAt: new Date().toISOString()
          }));
      }

      // Use order + 1 for chapter name
      const chapterNumber = order + 1;
      const chapterName = name || t('editor.chapter_name', { index: chapterNumber });

      const now = new Date().toISOString();
      const newChapter: NarrativeSummary = {
        id,
        name: chapterName,
        order,
        updatedAt: now,
      };

      // 1. Save all updates in batch (automatically updates summaries too)
      const allUpdates = [newChapter, ...shiftedChapters];
      await backend.narrative.batchSaveNarrativeItems(novel.ownerId, novel, 'chapters', allUpdates);

      toast({
        title: t('editor.chapter_created'),
        description: t('editor.chapter_created_desc', { name: chapterName }),
      });

      return id;
    } catch (error) {
      console.error('Create chapter error:', error);
      toast({
        title: t('common.errors.title'),
        description: t('common.errors.unknown'),
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  return { createChapter, isCreating };
}

/**
 * Hook to handle chapter updates (like name or order).
 */
export function useUpdateChapter(chapterId: string | null | undefined) {
  const { backend } = useFirebase();
  const { novel } = useNovel();
  const [isUpdating, setIsUpdating] = useState(false);

  const updateChapter = async (updates: Partial<NarrativeSummary>) => {
    if (!novel.id || !chapterId) return;

    setIsUpdating(true);
    try {
      const existingSummary = novel.summaries?.chapters?.[chapterId] as NarrativeSummary | undefined;
      if (!existingSummary) return;

      const summary = {
        ...existingSummary,
        ...updates,
      };

      // setNarrativeItem automatically saves to repo AND updates summaries
      await backend.narrative.setNarrativeItem(novel.ownerId, novel, 'chapters', summary);
    } catch (error) {
      console.error('Update chapter error:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return { updateChapter, isUpdating };
}

/**
 * Hook to handle chapter deletion.
 */
export function useDeleteChapter(chapterId: string | null | undefined) {
  const { backend } = useFirebase();
  const { novel } = useNovel();
  const { t } = useTranslation();
  const { toast: showToast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteChapter = async () => {
    if (!novel.id || !chapterId) return;

    setIsDeleting(true);
    try {
      // 1. Delete from repo & summary mirror
      await backend.narrative.deleteNarrativeItem(novel.ownerId, novel, 'chapters', chapterId);

      // 2. Unassign scenes
      const scenesToUnassign = Object.values(novel.summaries?.scenes || {})
        .map(s => s as NarrativeSummary)
        .filter(s => s.chapterId === chapterId);

      if (scenesToUnassign.length > 0) {
        const now = new Date().toISOString();
        const updates = scenesToUnassign.map(s => ({
          ...s,
          chapterId: '',
          updatedAt: now
        }));

        await backend.narrative.batchSaveNarrativeItems(novel.ownerId, novel, 'scenes', updates);
      }

      showToast({
        title: t('editor.chapter_deleted'),
        variant: 'destructive',
      });
    } catch (error) {
      console.error('Delete chapter error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const archiveChapter = async () => {
    if (!novel.id || !chapterId) return;
    setIsDeleting(true);
    try {
      const existingSummary = novel.summaries?.chapters?.[chapterId] as NarrativeSummary | undefined;
      if (!existingSummary) return;

      const summary = {
        ...existingSummary,
        isArchive: true,
      };
      await backend.narrative.setNarrativeItem(novel.ownerId, novel, 'chapters', summary);

      // Unassign scenes
      const scenesToUnassign = Object.values(novel.summaries?.scenes || {})
        .map(s => s as NarrativeSummary)
        .filter(s => s.chapterId === chapterId);

      if (scenesToUnassign.length > 0) {
        const now = new Date().toISOString();
        const updates = scenesToUnassign.map(s => ({
          ...s,
          chapterId: '',
          updatedAt: now
        }));

        await backend.narrative.batchSaveNarrativeItems(novel.ownerId, novel, 'scenes', updates);
      }

      showToast({
        title: t('editor.chapter_archived'),
      });
    } catch (error) {
      console.error('Archive chapter error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return { deleteChapter, archiveChapter, isDeleting };
}
